import type { GameSummary } from '@cardengine/shared';
import { linkProps } from '../router.js';

interface Control {
  keys: string;
  title: string;
  body: string;
}

const CONTROLS: Control[] = [
  {
    keys: '← →',
    title: 'Move',
    body: 'Hold either one and the piece keeps going, after a short pause. That pause is deliberate: it is what lets you nudge a piece one column without it sliding three.',
  },
  {
    keys: '↑ / X',
    title: 'Turn',
    body: 'Clockwise. Z turns it back the other way, which is usually the shorter route and almost always the one people forget they have.',
  },
  {
    keys: '↓',
    title: 'Soft drop',
    body: 'Falls twenty times faster while held, and pays a point a row. You keep full control the whole way down.',
  },
  {
    keys: 'space',
    title: 'Hard drop',
    body: 'Straight down to the outline, and locked on arrival. Two points a row, and no way back — the outline is showing you exactly where it lands, so there is nothing to guess.',
  },
  {
    keys: 'C',
    title: 'Hold',
    body: 'Puts the current piece aside and brings out whatever was there already. One swap per piece, so it is a place to keep an I bar, not a way to shuffle the queue.',
  },
  {
    keys: 'P',
    title: 'Pause',
    body: 'Stops the clock. Clicking away from the window pauses too, so a piece is never lost to a notification.',
  },
];

export function StackHowToPlay({ game }: { game: GameSummary }) {
  return (
    <main className="page page--prose">
      <header className="prose__head">
        <p className="prose__eyebrow">How to play</p>
        <h1 className="prose__title">{game.name}</h1>
        <p className="prose__lead">
          Fill a row all the way across and it disappears. The well never stops filling, so this is
          not a game you win — it is a game you keep tidy for as long as you can, and the score is
          the record of how tidy.
        </p>
      </header>

      <section className="prose__section">
        <h2>The controls</h2>
        <ul className="rulelist">
          {CONTROLS.map((control) => (
            <li key={control.keys} className="rulelist__item">
              <span className="rulelist__rank" aria-hidden="true">
                {control.keys}
              </span>
              <div>
                <h3>{control.title}</h3>
                <p>{control.body}</p>
              </div>
            </li>
          ))}
        </ul>
        <p className="prose__note">
          On a phone the buttons are down either side of the well. A and D move, W and S do the same
          job as the up and down arrows, and Z is the second turn wherever you are.
        </p>
      </section>

      <section className="prose__section">
        <h2>Why four at once</h2>
        <p>
          One row is a hundred points. Four rows taken together are eight hundred — not four hundred,
          which is the whole game in one number. Nothing stops you clearing rows one at a time, and
          you will last about as long doing it; you just will not have anything to show for it.
        </p>
        <p>
          So the well is dug rather than filled: nine columns kept flat and the tenth left open,
          waiting for the long straight piece. That is also why it hurts when it does not come, and
          why the <strong>hold</strong> slot exists — an I bar put away early is a hole you can
          afford to leave open.
        </p>
        <p>
          Keep the hard clears coming and they are worth half as much again on top. A four-row clear,
          or a T-spin, followed by another with nothing ordinary in between, is{' '}
          <strong>back to back</strong>. Clearing something on consecutive pieces builds a{' '}
          <strong>combo</strong> as well, which pays separately.
        </p>
      </section>

      <section className="prose__section">
        <h2>The pieces come in sevens</h2>
        <p>
          Not at random. All seven shapes are shuffled and dealt, then all seven again — so you will
          never wait more than twelve pieces for the one you want, and you can count. A well built on
          the assumption that an I bar is coming is a reasonable bet here, which under a free-running
          random it would not be.
        </p>
        <p>
          Three pieces of the queue are shown. That is enough to plan the next two placements
          properly, which is roughly as far ahead as anyone can hold the board in their head anyway.
        </p>
      </section>

      <section className="prose__section">
        <h2>Turning against the wall</h2>
        <p>
          A piece that has no room to turn where it stands will shove itself sideways or up and turn
          there, trying a handful of positions in a fixed order and giving up if none of them fit.
          This is why a piece against the left wall still turns, and why one can sometimes be
          screwed into a gap it plainly could not have been dropped into.
        </p>
        <p>
          The one worth learning is the <strong>T-spin</strong>: rotate a T into a notch so that
          three of the four corners around it are blocked, and the rows it clears are worth roughly
          twice what they would be otherwise. A T-spin that clears nothing at all still pays,
          because the hard part was getting it in there.
        </p>
      </section>

      <section className="prose__section">
        <h2>Landing is not quite locking</h2>
        <p>
          A piece that touches down gets about half a second before it sets, and moving or turning it
          gives that half second back — up to fifteen times, after which it locks wherever it is.
          That window is what makes sliding a piece under an overhang possible at speed, and the
          limit is what stops you living in it forever.
        </p>
        <p>
          The game ends when the next piece has nowhere to appear. There are two rows above the well
          that you never see and always play in, so that ending is a fact rather than a judgement.
        </p>
      </section>

      <div className="prose__cta">
        <a className="btn btn--primary btn--large" {...linkProps(game.href)}>
          Play {game.name}
        </a>
      </div>
    </main>
  );
}
